import {
    AfterViewInit,
    Directive,
    ElementRef,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    PLATFORM_ID,
    Renderer2
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';

/**
 * Fades an element in once it scrolls into view.
 * Falls back to the visible state whenever IntersectionObserver is unavailable
 * (server-side rendering, legacy browsers), so content is never hidden.
 */
@Directive({
    selector: '[rsReveal]'
    })
export class RevealDirective implements AfterViewInit, OnDestroy {
    /** Delay in milliseconds before the element animates in. */
    @Input('rsReveal') delay: number | string = 0;

    @HostBinding('class.rs-reveal') readonly revealClass = true;

    private observer?: IntersectionObserver;

    constructor(private elementRef: ElementRef<HTMLElement>,
                private renderer: Renderer2,
                @Inject(PLATFORM_ID) private platformId: object) {
    }

    ngAfterViewInit(): void {
        const element: HTMLElement = this.elementRef.nativeElement;

        if (!isPlatformBrowser(this.platformId) || typeof IntersectionObserver === 'undefined') {
            this.renderer.addClass(element, 'is-visible');
            return;
        }

        const delay = Number(this.delay) || 0;

        if (delay > 0) {
            this.renderer.setStyle(element, 'transition-delay', `${delay}ms`);
        }

        this.observer = new IntersectionObserver((entries: IntersectionObserverEntry[]): void => {
            entries.forEach((entry: IntersectionObserverEntry): void => {
                if (!entry.isIntersecting) {
                    return;
                }

                this.renderer.addClass(element, 'is-visible');
                this.observer?.unobserve(entry.target);
            });
        }, {threshold: 0.12, rootMargin: '0px 0px -8% 0px'});

        this.observer.observe(element);
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }
}
